import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { config, assertRequiredConfig } from './config';
import { log } from './logger';

log.debug('🔧 Supabase config:', {
  url: config.supabaseUrl ? '✅ Set' : '❌ Missing',
  key: config.supabaseAnonKey ? '✅ Set' : '❌ Missing',
  source: config.source,
});

try {
  assertRequiredConfig();
} catch (err) {
  log.error('❌ Supabase initialization failed:', err);
  throw err;
}

log.debug('✅ Creating Supabase client...');
export const supabase = createClient<Database>(config.supabaseUrl, config.supabaseAnonKey, {
  auth: {
    flowType: 'implicit',
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'mohit-auth',
    storage: window.localStorage
  }
});
log.debug('✅ Supabase client created successfully');

export function initSessionRefresh(): () => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const handler = () => {
    if (document.visibilityState !== 'visible') return;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      debounceTimer = null;
      log.debug('[Session] Page became visible, refreshing session...');
      try {
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          log.warn('[Session] Refresh failed:', error.message);
        } else {
          log.debug('[Session] Session refreshed successfully');
        }
      } catch (error) {
        log.error('[Session] Error refreshing session:', error);
      }
    }, 500);
  };

  document.addEventListener('visibilitychange', handler);
  log.debug('✅ Visibility change listener registered');

  return () => {
    document.removeEventListener('visibilitychange', handler);
    if (debounceTimer !== null) clearTimeout(debounceTimer);
  };
}
